import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Grid, Tab, Tabs, Typography } from '@mui/material';
import AppLayout from '../../layout/app.layout';
import { viewDataById } from '../../services/videos.service';
import ROUTE_PATH from '../../config/routes.config';
import Loading from '../../components/table-elements/loading.component';
import SnackbarInfo from '../../components/common/snackbar-info.component';
import PageNotFoundView from '../errors/not-found.view';
import ViewGridData from '../../components/table-elements/view-grid-data.component';
import {
  VIEW_AD_TIMING_COLUMNS,
  VIEW_ADMIN_COLUMNS,
  VIEW_ASSIGNMENTS_COLUMNS,
  VIEW_DISTRIBUTOR_COLUMNS,
  VIEW_EDITORIAL_COLUMNS,
  VIEW_METADATA_COLUMNS,
  VIEW_RATING_COLUMNS,
  VIEW_SCHEDULING_COLUMNS,
  VIEW_SERIES_COLUMNS,
  VIEW_STATUS_COLUMNS,
  VIEW_SYSTEM_ADMIN_COLUMNS,
} from '../../config/module-configs/content-videos.config';
import useAccess from '../../hooks/access.hook';
import Iconify from '../../components/common/iconify.component';
import replaceIdUrl from '../../utils/url.util';

const TABS_CONFIG = [
  {
    value: 'metadata',
    label: 'Profile',
    columns: VIEW_METADATA_COLUMNS,
  },
  {
    value: 'series',
    label: 'Series',
    columns: VIEW_SERIES_COLUMNS,
  },
  {
    value: 'systemAdmin',
    label: 'Assets',
    columns: VIEW_SYSTEM_ADMIN_COLUMNS,
  },
  {
    value: 'distributor',
    label: 'Owner',
    columns: VIEW_DISTRIBUTOR_COLUMNS,
  },
  {
    value: 'scheduling',
    label: 'Rights Management',
    columns: VIEW_SCHEDULING_COLUMNS,
  },
  { value: 'adTiming', label: 'Ad Timing', columns: VIEW_AD_TIMING_COLUMNS },
  { value: 'editorial', label: 'Editorial', columns: VIEW_EDITORIAL_COLUMNS },
  {
    value: 'rating',
    label: 'Rating',
    columns: VIEW_RATING_COLUMNS,
  },
  {
    value: 'assignments',
    label: 'Assignment',
    columns: VIEW_ASSIGNMENTS_COLUMNS,
  },
  { value: 'status', label: 'Status', columns: VIEW_STATUS_COLUMNS },
  { value: 'admin', label: 'Admin', columns: VIEW_ADMIN_COLUMNS },
  // { value: 'imdb', label: 'IMDB Settings', columns: VIEW_IMDB_COLUMNS },
  // { value: 'others', label: 'Others', columns: VIEW_OTHERS_COLUMNS },
];

const VideoViewDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasOperationAccess } = useAccess();
  const [tabValue, setTabValue] = useState(TABS_CONFIG[0].value);
  const [loading, setLoading] = useState(true);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [showPageNotFound, setShowPageNotFound] = useState(false);
  const [videoDetail, setVideoDetail] = useState({});
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleRefreshData = () => {
    setReloadCounter(reloadCounter + 1);
  };

  useEffect(() => {
    viewDataById(id)
      .then((res) => {
        setVideoDetail(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          setShowPageNotFound(true);
          return;
        }
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [id, reloadCounter]);

  return Number.isNaN(Number(id)) || showPageNotFound ? (
    <PageNotFoundView />
  ) : (
    <AppLayout
      pageTitle={`View Video Details - ${videoDetail.title}`}
      showBack
      handleBack={() => navigate(ROUTE_PATH.VIDEOS)}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        my={1}
      >
        <Grid container spacing={0.5} mx={1} width={500}>
          {!loading && (
            <>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.videoUrl && videoDetail.qualityVideo !== 'FA' ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  Video Asset
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.avodCleared ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  AVOD Cleared
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.offline === 'NO' ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  Online
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.poster16x9Url &&
                  videoDetail.qualityPoster16x9 !== 'FA' ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  Poster Asset
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.svodCleared ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  SVOD Cleared
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.liveOnAppsCount > 0 ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  Live on 1+ Apps
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.subtitleEnUrl &&
                  videoDetail.qualitySubtitle !== 'FA' ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  Subtitle Asset
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.tvodCleared ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  TVOD Cleared
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={4} />
              <Grid item xs={12} sm={4} md={4}>
                <Typography variant="subtitle2" display="flex">
                  {videoDetail.trailerUrl &&
                  videoDetail.qualityTrailer !== 'FA' ? (
                    <Iconify icon="twemoji:check-mark-button" mr={1} />
                  ) : (
                    <Iconify icon="emojione:cross-mark-button" mr={1} />
                  )}
                  Trailer Asset
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={8} />
            </>
          )}
        </Grid>
      </Box>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="flex-end"
        my={1}
      >
        {hasOperationAccess('video_library', 'edit') && (
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="bx:edit" />}
            onClick={() => {
              const url = replaceIdUrl(ROUTE_PATH.VIDEO_EDIT, id);
              navigate(url);
            }}
            disabled={loading}
          >
            Edit This Video
          </Button>
        )}
        <Button
          color="inherit"
          variant="contained"
          startIcon={<Iconify icon="ic:twotone-refresh" />}
          onClick={() => handleRefreshData()}
          disabled={loading}
          style={{ marginLeft: 5 }}
        >
          Refresh
        </Button>
      </Box>

      <Box height="100%" display="flex">
        <Tabs
          value={tabValue}
          aria-label="tabs"
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="transparent"
          sx={{ minWidth: 150, maxWidth: 150 }}
          onChange={(_, v) => setTabValue(v)}
        >
          {TABS_CONFIG.map((tab, index) => (
            <Tab
              key={index + 1}
              value={tab.value}
              label={tab.label}
              sx={{
                background: tabValue === tab.value && '#fff',
                borderLeft: tabValue === tab.value && '2px solid #FD0100',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            />
          ))}
        </Tabs>

        <Box flexGrow={1}>
          {loading && <Loading />}

          {!loading && (
            <ViewGridData
              columns={
                TABS_CONFIG.filter((tab) => tab.value === tabValue)[0]?.columns
              }
              row={videoDetail}
            />
          )}
        </Box>
      </Box>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </AppLayout>
  );
};

export default VideoViewDetails;
